import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { useSiteImages, pick } from "@/lib/siteImages";

const HERO_IMG =
  "https://images.pexels.com/photos/14509757/pexels-photo-14509757.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HERO_IMG_2 =
  "https://images.pexels.com/photos/29193428/pexels-photo-29193428.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const lines = [
  { text: "Custodisci i nomi", className: "text-[#37282B]" },
  { text: "dei tuoi bambini", className: "text-[#37282B]" },
  { text: "in un gioiello unico.", className: "italic text-[#E2A3B1]" },
];

export default function Hero() {
  const ref = useRef(null);
  const siteImgs = useSiteImages();
  const heroVideo = siteImgs?.hero_video;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const img2Y = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden grain-overlay bg-[#FDF8F7]" data-testid="hero-section">
      <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-[#E2A3B1]/10 blur-3xl" />
      <div className="absolute bottom-0 -left-40 w-[28rem] h-[28rem] rounded-full bg-[#E9B7C2]/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 grid lg:grid-cols-12 gap-12 items-center relative z-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#E2A3B1]" />
            <span className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C4758A]" data-testid="hero-tagline">
              Fatto a mano in Italia
            </span>
          </motion.div>

          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-medium" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                  className={line.className}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-8 max-w-md text-base sm:text-lg text-[#6B5459] leading-relaxed"
            data-testid="hero-subheading"
          >
            Pendenti personalizzati incisi a mano con il nome dei tuoi piccoli.
            Ogni creazione nasce nel nostro atelier, pensata per celebrare i
            momenti più preziosi della tua famiglia.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#configura"
              data-testid="hero-cta-personalizza"
              className="group px-8 py-4 bg-[#37282B] text-[#FDF8F7] text-xs uppercase tracking-[0.2em] font-semibold rounded-full inline-flex items-center gap-2 hover:bg-[#E2A3B1] hover:text-[#37282B] transition-colors duration-300"
            >
              <Sparkles className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
              Personalizza il tuo Pendente
            </a>
            <a
              href="#collezioni"
              data-testid="hero-cta-collezione"
              className="px-8 py-4 border border-[#37282B]/20 text-[#37282B] text-xs uppercase tracking-[0.2em] font-semibold rounded-full hover:border-[#E2A3B1] hover:text-[#C4758A] transition-colors duration-300"
            >
              Esplora la Collezione
            </a>
          </motion.div>
        </div>

        <div className={`lg:col-span-5 relative ${heroVideo ? "" : "hidden lg:block"}`}>
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="rounded-t-[10rem] rounded-b-2xl overflow-hidden shadow-2xl shadow-[#37282B]/20">
              {heroVideo ? (
                <video
                  src={heroVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={pick(siteImgs, "hero_main", HERO_IMG)}
                  className="w-full h-80 sm:h-96 lg:h-[32rem] object-cover"
                  data-testid="hero-video"
                />
              ) : (
                <img
                  src={pick(siteImgs, "hero_main", HERO_IMG)}
                  alt="Pendente artigianale Ma.Vi Gioielli"
                  className="w-full h-[32rem] object-contain"
                  data-testid="hero-image"
                />
              )}
            </motion.div>
            <motion.div
              style={{ y: img2Y }}
              className="absolute -bottom-10 -left-16 w-44 h-56 rounded-2xl overflow-hidden border-4 border-[#FDF8F7] shadow-xl shadow-[#37282B]/20 animate-float-slow bg-[#FDF8F7]"
            >
              <img
                src={pick(siteImgs, "hero_secondary", HERO_IMG_2)}
                alt="Dettaglio pendente con nome"
                className="w-full h-full object-contain"
                data-testid="hero-image-secondary"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#collezioni"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A89095] hover:text-[#E2A3B1] transition-colors"
        data-testid="hero-scroll-indicator"
        aria-label="Scorri giù"
      >
        <ArrowDown className="w-5 h-5 animate-bounce" strokeWidth={1.5} />
      </motion.a>
    </section>
  );
}
